// import OnBoardingSkeleton from '@/components/on-boarding/Skeleton';
import { useAdminStore } from '../store/useAdmin.store';
import { useEffect, useMemo, useState } from 'react';
import 'react-quill/dist/quill.snow.css';
import { useAuthentication } from '@/hooks/useAuthentication';
import { downloadPDF, inviteStaff, importStaff } from '@/services/global/admin.service';
import { useToast } from '@/components/ui/use-toast';
import { RowData } from '../components/spreadsheet';
import { FetchMySchoolsQueryVariables, FetchMySchoolsQuery } from '@/graphql/generated/graphql';
import { useQuery } from '@apollo/client';
import { QUERY_SCHOOL_CHILDREN } from '@/graphql/queries/dashboard/dashboard';

export function useSchoolManagement() {
  const { data: schools } = useQuery<FetchMySchoolsQuery, FetchMySchoolsQueryVariables>(QUERY_SCHOOL_CHILDREN, {
    variables: {
      asRegularUser: true,
    },
  });

  const { page, onInit } = useAdminStore();
  const [template, setTemplate] = useState('');
  const [loadingPDF, setLoadingPDF] = useState(false);
  const [loadingInvite, setLoadingInvite] = useState(false);
  const [loadingImport, setLoadingImport] = useState(false);
  const [loadingSendInvitation, setLoadingSendInvitation] = useState(false);
  const { authentication } = useAuthentication();
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [usersPreview, setUsersPreview] = useState<RowData[]>([]);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [schoolId, setSchoolId] = useState<string | null>(
    authentication?.user?.school?.id ? authentication.user.school.id.toString() : null,
  );
  const { toast } = useToast();

  const forename = authentication.user.forename || 'Guest';
  const school = useMemo(() => {
    return schools?.fetchMySchools?.find((school) => school.id === schoolId)?.attributes as {
      name: string;
      code: string;
    };
  }, [schools, schoolId]);

  useEffect(() => {
    if (!page) onInit();
  }, [onInit, page]);

  useEffect(() => {
    const template = page?.schoolManagement?.template
      ? page?.schoolManagement?.template
          .replace(/\${schoolName}/g, school?.name)
          .replace(/\${uniqueCode}/g, school?.code)
      : '';
    setTemplate(template || '');
  }, [page, school]);

  const onShowInvitationPreview = async () => {
    setLoadingInvite(true);
    const { data, error } = await inviteStaff(Number(schoolId), true);
    if (error) {
      toast({
        title: page?.schoolManagement?.toastStaffInvitedTitle || '',
        description: error.message,
        variant: 'destructive',
      });
      setLoadingInvite(false);
      return;
    }

    setUsersPreview(data.importedUsers);
    setIsPreviewDialogOpen(true);
    setLoadingInvite(false);
  };

  const onSendInvitationEmail = async () => {
    setLoadingSendInvitation(true);
    const { data, error } = await inviteStaff(Number(schoolId), false);
    if (error) {
      toast({
        title: page?.schoolManagement?.toastStaffInvitedTitle || '',
        description: error.message,
        variant: 'destructive',
      });
      setLoadingSendInvitation(false);
      return;
    }

    toast({
      title: page?.schoolManagement?.toastStaffInvitedTitle || '',
      description: `${data.size} ${page?.schoolManagement?.toastStaffInvitedDescription || ''}`,
    });
    setIsPreviewDialogOpen(false);
    setLoadingSendInvitation(false);
  };

  const onImportStaff = async (users?: RowData[]) => {
    setLoadingImport(true);
    const { data, error } = await importStaff(Number(schoolId), users);

    if (error) {
      toast({
        title: page?.schoolManagement?.toastStaffImportedTitle || '',
        description: error.message,
        variant: 'destructive',
      });
      setLoadingImport(false);
      return;
    }

    toast({
      title: page?.schoolManagement?.toastStaffImportedTitle || '',
      description: `${data.size} ${page?.schoolManagement?.toastStaffImportedDescription || ''}`,
    });
    setLoadingImport(false);
  };

  const downloadQRCodePDF = async () => {
    setLoadingPDF(true);
    const { data, error } = await downloadPDF(school?.code, school?.name);

    if (error) {
      toast({
        title: page?.schoolManagement?.toastErrorDownloadingPDFTitle || '',
        description: error.message,
        variant: 'destructive',
      });
      setLoadingPDF(false);
      return;
    }

    window.open(data.url, '_blank');

    toast({
      title: page?.schoolManagement?.toastPDFDownloadedTitle || '',
      description: page?.schoolManagement?.toastPDFDownloadedDescription || '',
    });
    setLoadingPDF(false);
  };

  return {
    page,
    forename,
    school,
    tooltipVisible,
    loadingInvite,
    loadingImport,
    loadingPDF,
    loadingSendInvitation,
    usersPreview,
    isPreviewDialogOpen,
    template,
    schoolId,
    user: authentication.user,
    setSchoolId,
    setTemplate,
    setIsPreviewDialogOpen,
    setTooltipVisible,
    onShowInvitationPreview,
    onSendInvitationEmail,
    onImportStaff,
    downloadQRCodePDF,
  };
}
